import { BillingApi, UI } from "@/types";

export type BankAccount = BillingApi.BankAccount;
export type APISingleResponse<T> = UI.APISingleResponse<T>;

export type AccountActivationRequests = {
    loadAccountActivationDetails: (token: string) => Promise<APISingleResponse<BillingApi.AccountActivationDetails>>,
    saveAccountActivationBasicData: (params: {[key: string]: string}) => Promise<void>,
    accountActivationPaymentAccountCreate: (
        params: BillingApi.PaymentAccountCreateParameters
    ) => Promise<APISingleResponse<BillingApi.StripePaymentStartResult>>,
    accountActivationPaymentAccountCommit: (paymentAccountId: string) => Promise<void>,
    accountActivationPaymentAccountCancel: (paymentAccountId: string) => Promise<void>,
    createBankAccount: (
        bankAccountCreateData: BankAccountCreateData,
    ) => Promise<APISingleResponse<BankAccount>>,
    accountActivationSetAdvertisingOptIn: (advertisingOptIn: boolean) => Promise<void>,
    accountActivationPaymentPrepaid: () => Promise<void>,
}

export type BankAccountCreateData = {
    iban: string,
    accountHolder: string,
    bic: string,
}

export type Loading = {
    state: 'loading';
};

export type Waiting = {
    state: 'waiting';
};

export type Loaded<T> = {
    state: 'loaded';
    data: T;
};
export type MaybeLoading<T> = Loading | Waiting | Loaded<T>;

export class AccountActivation {

    public userData: MaybeLoading<BillingApi.AccountActivationDetails> = { state: 'waiting' };

    constructor(
        protected token: string,
        protected requests: AccountActivationRequests
    ) {
    }

    public loadAccountActivationDetails(): Promise<void> {
        return this.requests.loadAccountActivationDetails(this.token)
            .then((result) => {
                this.userData = { state: 'loaded', data: result.response};
            }).catch((err) => Promise.reject(err));
    }

    public saveAccountActivationBasicData(parameters: {[key: string]: string}): Promise<void> {
        return this.requests.saveAccountActivationBasicData(parameters)
            .then((result) => result)
            .catch((err) => Promise.reject(err));
    }

    public accountActivationPaymentAccountCreate(params: BillingApi.PaymentAccountCreateParameters): Promise<UI.APISingleResponse<BillingApi.StripePaymentStartResult>> {
        return this.requests.accountActivationPaymentAccountCreate(params)
            .then((result) => result)
            .catch((err) => Promise.reject(err));
    }

    public accountActivationPaymentAccountCommit(paymentAccountId: string): Promise<void> {
        return this.requests.accountActivationPaymentAccountCommit(paymentAccountId)
            .then((result) => result)
            .catch((err) => Promise.reject(err));
    }

    public accountActivationPaymentAccountCancel(paymentAccountId: string): Promise<void> {
        return this.requests.accountActivationPaymentAccountCancel(paymentAccountId)
            .then((result) => result)
            .catch((err) => Promise.reject(err));
    }

    public createBankAccount(
        bankAccountCreateData: BankAccountCreateData,
    ): Promise<APISingleResponse<BankAccount>> {
        return this.requests.createBankAccount(bankAccountCreateData)
            .then((result) => result)
            .catch((err) => Promise.reject(err));
    }

    public accountActivationSetAdvertisingOptIn(advertisingOptIn: boolean): Promise<void> {
        return this.requests.accountActivationSetAdvertisingOptIn(advertisingOptIn)
            .then((result) => result)
            .catch((err) => Promise.reject(err));
    }

    public accountActivationPaymentPrepaid(): Promise<void> {
        return this.requests.accountActivationPaymentPrepaid()
            .then((result) => result)
            .catch((err) => Promise.reject(err));
    }

    public createPaymentAccount(paymentMethod: string): Promise<UI.APISingleResponse<BillingApi.StripePaymentStartResult>> {
        let paymentParameters: BillingApi.PaymentAccountCreateParameters;
        const baseUrl = window.location.protocol + '//' + window.location.host + '/';
        let commitUrl = baseUrl + 'account-activation/' + this.token + '/commit/';
        let cancelUrl = baseUrl + 'account-activation/' + this.token + '/cancel/';
        commitUrl += 'stripe';
        cancelUrl += 'stripe';
        switch (paymentMethod) {
            case 'card':
                paymentParameters = {
                    cancelUrl,
                    commitUrl,
                    types: ['card']
                };
                break;
            case 'paypal':
                paymentParameters = {
                    cancelUrl,
                    commitUrl,
                    types: ['paypal']
                };
                break;
            case 'others':
                paymentParameters = {
                    cancelUrl,
                    commitUrl,
                    types: [
                        'card',
                        'ideal',
                        'bancontact',
                        'amazonPay'
                    ]
                };
                break;
            default:
                // TODO error handling
                return Promise.reject('payment method obscured');
        }
        return this.accountActivationPaymentAccountCreate(paymentParameters);
    }

    public loadStripeScript() {
        // const stripeScriptUrl = 'https://checkout.stripe.com/checkout.js';
        const stripeScriptUrl = 'https://js.stripe.com/v3/';
        let isFound = false;
        const scripts = document.getElementsByTagName('script');

        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < scripts.length; ++i) {
            if (
                scripts[i].getAttribute('src') != null
                && scripts[i].getAttribute('src').indexOf(stripeScriptUrl) >= 0
            ) {
                isFound = true;
            }
        }

        if (!isFound) {
            const dynamicScripts = [stripeScriptUrl];

            // tslint:disable-next-line:prefer-for-of
            for (let i = 0; i < dynamicScripts .length; i++) {
                const node = document.createElement('script');
                node.src = dynamicScripts [i];
                node.type = 'text/javascript';
                node.async = false;
                node.charset = 'utf-8';
                document.getElementsByTagName('head')[0].appendChild(node);
            }
        }
    }


    public get userDetails() {
        if (this.userData.state === 'loaded') {
            return this.userData.data;
        }
        // TODO
        return;
    }

}
